<script setup>
defineOptions({ name: "PixModulePage" });
import { defineAsyncComponent } from "vue";
import {
  QrCodeIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  ClockIcon,
  TruckIcon,
  HeartIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/vue/24/outline";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import heroBg from "@/assets/hero-pix.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const portalUrl =
  import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

useSeo({
  title: "O cliente paga. O Kali Gestão confirma. | Kali Sistemas",
  description:
    "Gere um QR Code Pix com o valor exato da venda e acompanhe a confirmação do pagamento diretamente no PDV do Kali Gestão, sem conferência manual.",
  image: heroBg,
});

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// Capacidades reais do adicional PIX, descritas no material do produto.
const capacidades = [
  {
    icon: QrCodeIcon,
    titulo: "Valor exato",
    texto: "O valor da venda já faz parte da cobrança gerada automaticamente.",
  },
  {
    icon: CheckCircleIcon,
    titulo: "Confirmação",
    texto: "O pagamento é acompanhado por meio da integração bancária.",
  },
  {
    icon: ClipboardDocumentCheckIcon,
    titulo: "Menos conferência",
    texto: "Reduza a necessidade de consultar aplicativos externos.",
  },
  {
    icon: ShoppingCartIcon,
    titulo: "Venda integrada",
    texto: "O pagamento faz parte do fluxo da própria operação.",
  },
];

const steps = [
  {
    title: "Venda é realizada",
    description: "O operador lança os produtos normalmente no PDV.",
  },
  {
    title: "Cliente escolhe Pix",
    description: "O Pix é selecionado como forma de pagamento.",
  },
  {
    title: "QR Code é gerado",
    description: "O sistema cria uma cobrança relacionada ao valor da venda.",
  },
  {
    title: "Cliente paga",
    description: "O cliente escaneia o QR Code utilizando o aplicativo do banco.",
  },
  {
    title: "Banco confirma e a venda é liberada",
    description: "A confirmação retorna para o sistema e o fluxo segue para conclusão.",
  },
];

// Operações que se beneficiam de pagamento instantâneo e caixa sem conferência manual.
const segmentos = [
  {
    icon: ShoppingBagIcon,
    titulo: "Varejo",
    texto: "Vendas de balcão com pagamento rápido.",
  },
  {
    icon: BuildingStorefrontIcon,
    titulo: "Supermercados",
    texto: "Alto volume de vendas com Pix no caixa.",
  },
  {
    icon: ClockIcon,
    titulo: "Restaurantes",
    texto: "Atendimento ágil na hora de fechar a conta.",
  },
  {
    icon: TruckIcon,
    titulo: "Delivery",
    texto: "Pagamento confirmado antes da saída do pedido.",
  },
  {
    icon: HeartIcon,
    titulo: "Pet shops",
    texto: "Atendimento rápido no caixa.",
  },
  {
    icon: WrenchScrewdriverIcon,
    titulo: "Prestadores de serviços",
    texto: "Recebimento rápido ao concluir o atendimento.",
  },
];

const faqItems = [
  {
    question: "Qual a diferença entre o Pix comum e o Pix integrado?",
    answer:
      "No Pix comum, a empresa pode precisar utilizar uma chave ou QR fixo e conferir manualmente o pagamento. No Pix integrado, a cobrança está relacionada à venda e o sistema acompanha a confirmação do pagamento.",
  },
  {
    question: "O valor precisa ser digitado manualmente?",
    answer:
      "Não. O QR Code dinâmico utiliza o valor relacionado à própria venda, reduzindo etapas de digitação.",
  },
  {
    question: "O operador precisa conferir o aplicativo do banco?",
    answer:
      "A integração permite acompanhar o retorno do pagamento dentro do fluxo do sistema.",
  },
  {
    question: "O recebimento também chega ao financeiro?",
    answer:
      "Sim. O fluxo de pagamento permanece conectado ao controle financeiro do Kali Gestão.",
  },
  {
    question: "Como solicito a ativação do módulo?",
    answer:
      "Pelo botão \"Solicitar ativação\" desta página, que leva direto ao Portal do Cliente.",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white">
    <SegmentHero title="O cliente paga. O Kali Gestão confirma."
      description="Gere um QR Code Pix com o valor exato da venda e acompanhe a confirmação do pagamento diretamente no PDV. Sem precisar consultar o aplicativo do banco ou conferir comprovantes manualmente."
      primary-label="Solicitar ativação" :primary-href="portalUrl" :primary-opens-demo="false"
      secondary-label="Ver Kali Gestão" secondary-href="/solucao/kali-gestao" :background-image="heroBg"
      background-class="bg-slate-900 bg-cover bg-center" overlay-variant="left" />

    <!-- Visão geral -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Pix qualquer sistema aceita. O diferencial é integrar o pagamento à venda.
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Receber Pix por uma chave ou QR Code fixo normalmente exige que
            o operador confirme manualmente se o dinheiro realmente entrou.
            Com o Pix integrado ao Kali Gestão, a cobrança faz parte do
            próprio fluxo do PDV: o valor é enviado corretamente, o
            pagamento é acompanhado e a venda segue após a confirmação.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="cap in capacidades" :key="cap.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="cap.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ cap.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ cap.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Como funciona -->
    <section class="py-16 bg-gray-100 scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Da venda à confirmação em um único fluxo
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Sem consulta ao banco. Sem confirmação manual.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-5">
          <div v-for="(step, idx) in steps" :key="step.title" v-reveal class="p-7 bg-white rounded-3xl shadow-sm">
            <div
              class="inline-flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-kali-blue-600">
              {{ idx + 1 }}
            </div>
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>

      </div>
    </section>

    <!-- Ideal para -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Mais uma opção de pagamento sem complicar o caixa
          </h2>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="s in segmentos" :key="s.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="s.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ s.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ s.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <FaqPanel title="Dúvidas sobre PIX" :items="faqItems" cta-label="Solicitar ativação" :cta-href="portalUrl"
      description="Respostas rápidas sobre o módulo PIX. Se preferir, fale com nosso time e te orientamos no melhor cenário." />

    <CTA :activation-href="portalUrl" />
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
